import { useState } from "react"
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import { toast } from "sonner"
import { Search, CreditCard, Plus } from "lucide-react"

import * as api from "@/api/cogeser"
import { useAuth } from "@/hooks/useAuth"
import type { ICustomer, ILoyaltyCard, ILoyaltyTransaction } from "@/types"

// ----------------------------------------------------------------------------

export default function Fidelity() {
  // Hooks
  const { token } = useAuth()
  const queryClient = useQueryClient()
  const [searchQuery, setSearchQuery] = useState("")
  const [searchTerm, setSearchTerm] = useState("")

  // Search customers by email or customerCode
  const { data: searchResults, isLoading: isSearching } = useQuery({
    queryKey: ["fidelity-search", searchTerm],
    queryFn: () =>
      api.getCustomers({ search: searchTerm, limit: 10 }, token!) as Promise<{ data: ICustomer[] }>,
    enabled: !!token && searchTerm.length >= 2,
  })

  const customers = searchResults?.data || []

  // Selected customer for fidelity management
  const [selectedCustomer, setSelectedCustomer] = useState<ICustomer | null>(null)

  function handleSearch(e: React.FormEvent) {
    e.preventDefault()
    setSearchTerm(searchQuery.trim())
    setSelectedCustomer(null)
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Punti Fidelity</h1>
        <p className="mt-1 text-sm text-gray-500">
          Cerca un'anagrafica per gestire i punti fidelity
        </p>
      </div>

      {/* Search */}
      <form onSubmit={handleSearch} className="flex gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Cerca per email o codice cliente..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full rounded-lg border border-gray-200 bg-white py-2.5 pl-10 pr-4 text-sm outline-none transition-colors focus:border-primary focus:ring-2 focus:ring-primary/20"
          />
        </div>
        <button
          type="submit"
          className="rounded-lg bg-primary px-5 py-2.5 text-sm font-medium text-primary-foreground hover:bg-primary/90"
        >
          Cerca
        </button>
      </form>

      {/* Search Results */}
      {searchTerm && !selectedCustomer && (
        <div className="overflow-hidden rounded-xl border border-gray-200 bg-white">
          {isSearching ? (
            <div className="px-6 py-8 text-center text-sm text-gray-500">Ricerca...</div>
          ) : customers.length === 0 ? (
            <div className="px-6 py-8 text-center text-sm text-gray-500">
              Nessun risultato per "{searchTerm}"
            </div>
          ) : (
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200 bg-gray-50">
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Codice</th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Nome</th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Email</th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">CF</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {customers.map((c) => (
                  <tr
                    key={c.id}
                    className="cursor-pointer hover:bg-gray-50"
                    onClick={() => setSelectedCustomer(c)}
                  >
                    <td className="px-6 py-3">
                      <span className="rounded-md bg-gray-100 px-2 py-0.5 text-xs font-mono font-medium text-gray-700">
                        {c.customerCode}
                      </span>
                    </td>
                    <td className="px-6 py-3 text-sm font-medium text-gray-900">
                      {c.surname} {c.name}
                    </td>
                    <td className="px-6 py-3 text-sm text-gray-600">{c.email || "-"}</td>
                    <td className="px-6 py-3 text-sm font-mono text-gray-600">{c.fiscalCode}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* Selected Customer — Fidelity Card */}
      {selectedCustomer && (
        <FidelityPanel
          customer={selectedCustomer}
          token={token!}
          onBack={() => setSelectedCustomer(null)}
        />
      )}
    </div>
  )
}

// ============================================================================
// Fidelity Panel (card + transactions + add points)
// ============================================================================

function FidelityPanel({
  customer,
  token,
  onBack,
}: {
  customer: ICustomer
  token: string
  onBack: () => void
}) {
  const queryClient = useQueryClient()
  const [showAddPoints, setShowAddPoints] = useState(false)
  const [pointsAmount, setPointsAmount] = useState("")
  const [causale, setCausale] = useState("")

  // Fetch loyalty card
  const { data: cardsData, isLoading: isLoadingCard } = useQuery({
    queryKey: ["fidelity-card", customer.id],
    queryFn: () =>
      api.getLoyaltyCards({ customer: customer.id }, token) as Promise<{ data: ILoyaltyCard[] }>,
  })

  const card = (cardsData?.data || [])?.[0] as ILoyaltyCard | undefined

  // Fetch transactions
  const { data: txData } = useQuery({
    queryKey: ["fidelity-transactions", card?.id],
    queryFn: () => api.getCardTransactions(card!.id, { limit: 50 }, token),
    enabled: !!card?.id,
  })

  const transactions = ((txData as any)?.data || []) as ILoyaltyTransaction[]

  // Adjust points
  const adjustMutation = useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      api.adjustPoints(card!.id, body, token),
    onSuccess: () => {
      toast.success("Punti aggiornati")
      queryClient.invalidateQueries({ queryKey: ["fidelity-card", customer.id] })
      queryClient.invalidateQueries({ queryKey: ["fidelity-transactions", card?.id] })
      setShowAddPoints(false)
      setPointsAmount("")
      setCausale("")
    },
    onError: () => toast.error("Errore nell'aggiornamento punti"),
  })

  function handleAddPoints(e: React.FormEvent) {
    e.preventDefault()
    const pts = parseInt(pointsAmount)
    if (isNaN(pts) || pts === 0) return
    if (!causale.trim()) {
      toast.error("La causale è obbligatoria")
      return
    }
    adjustMutation.mutate({
      points: pts,
      description: causale.trim(),
      ruleCode: "admin_manual",
      ruleLabel: pts > 0 ? "Aggiunta manuale" : "Riduzione manuale",
    })
  }

  if (isLoadingCard) {
    return <div className="py-8 text-center text-sm text-gray-500">Caricamento...</div>
  }

  return (
    <div className="space-y-6">
      {/* Customer header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10 text-sm font-semibold text-primary">
            {customer.name?.charAt(0)?.toUpperCase() || "?"}
          </div>
          <div>
            <h2 className="text-lg font-semibold text-gray-900">
              {customer.surname} {customer.name}
            </h2>
            <p className="text-sm text-gray-500">
              Cod. {customer.customerCode} · CF {customer.fiscalCode}
            </p>
          </div>
        </div>
        <button
          onClick={onBack}
          className="rounded-lg border border-gray-200 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
        >
          Nuova ricerca
        </button>
      </div>

      {!card ? (
        <div className="rounded-xl border border-gray-200 bg-white p-8 text-center">
          <CreditCard className="mx-auto h-10 w-10 text-gray-300" />
          <p className="mt-3 text-sm text-gray-500">Nessuna fidelity card associata</p>
        </div>
      ) : (
        <>
          {/* Card Summary */}
          <div className="rounded-xl border border-gray-200 bg-white">
            <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
              <h3 className="text-base font-semibold text-gray-900">Fidelity Card</h3>
              <span
                className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${
                  card.status === "active" ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-600"
                }`}
              >
                {card.status}
              </span>
            </div>
            <div className="grid grid-cols-1 gap-4 p-6 sm:grid-cols-3">
              <div>
                <p className="text-xs font-medium uppercase text-gray-500">Saldo Punti</p>
                <p className="mt-1 text-2xl font-bold text-primary">{card.balance}</p>
              </div>
              <div>
                <p className="text-xs font-medium uppercase text-gray-500">Codice Referral</p>
                <p className="mt-1 text-sm font-mono text-gray-900">{card.referralCode || "-"}</p>
              </div>
              <div className="flex items-end">
                <button
                  onClick={() => setShowAddPoints(true)}
                  className="flex items-center gap-1.5 rounded-lg bg-primary px-3 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
                >
                  <Plus className="h-4 w-4" /> Movimenta punti
                </button>
              </div>
            </div>
          </div>

          {/* Add Points Form */}
          {showAddPoints && (
            <div className="rounded-xl border border-primary/20 bg-primary/5 p-4">
              <form onSubmit={handleAddPoints} className="space-y-3">
                <div className="flex flex-wrap gap-3">
                  <div className="space-y-1.5">
                    <label className="block text-sm font-medium text-gray-700">Punti (+/-)</label>
                    <input
                      type="number"
                      value={pointsAmount}
                      onChange={(e) => setPointsAmount(e.target.value)}
                      required
                      placeholder="es. 100 o -50"
                      className="w-36 rounded-lg border border-gray-300 px-3 py-2 text-sm outline-none focus:border-primary focus:ring-2 focus:ring-primary/20"
                    />
                  </div>
                  <div className="flex-1 space-y-1.5">
                    <label className="block text-sm font-medium text-gray-700">
                      Causale <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={causale}
                      onChange={(e) => setCausale(e.target.value)}
                      required
                      placeholder="Motivo della movimentazione (visibile in app)"
                      className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm outline-none focus:border-primary focus:ring-2 focus:ring-primary/20"
                    />
                  </div>
                </div>
                <div className="flex gap-3">
                  <button
                    type="submit"
                    disabled={adjustMutation.isPending}
                    className="rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
                  >
                    {adjustMutation.isPending ? "Invio..." : "Conferma"}
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowAddPoints(false)}
                    className="rounded-lg border border-gray-200 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                  >
                    Annulla
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* Transactions */}
          <div className="overflow-hidden rounded-xl border border-gray-200 bg-white">
            <div className="border-b border-gray-200 px-6 py-4">
              <h3 className="text-base font-semibold text-gray-900">Storico Movimentazioni</h3>
            </div>
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200 bg-gray-50">
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Data</th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Causale</th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Fonte</th>
                  <th className="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">Punti</th>
                  <th className="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">Saldo</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {transactions.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="px-6 py-8 text-center text-sm text-gray-500">
                      Nessuna movimentazione
                    </td>
                  </tr>
                ) : (
                  transactions.map((tx) => (
                    <tr key={tx.id} className="hover:bg-gray-50">
                      <td className="px-6 py-3 text-sm text-gray-600">
                        {new Date(tx.createdAt).toLocaleDateString("it-IT")}
                      </td>
                      <td className="px-6 py-3 text-sm text-gray-900">
                        {tx.ruleLabel}
                        {tx.description ? ` — ${tx.description}` : ""}
                      </td>
                      <td className="px-6 py-3">
                        <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600">
                          {tx.source}
                        </span>
                      </td>
                      <td
                        className={`px-6 py-3 text-right text-sm font-medium ${
                          tx.points > 0 ? "text-green-600" : "text-red-600"
                        }`}
                      >
                        {tx.points > 0 ? "+" : ""}
                        {tx.points}
                      </td>
                      <td className="px-6 py-3 text-right text-sm text-gray-600">
                        {tx.balanceAfter}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  )
}
